<template>
  <div :id="panoId" class="pano"></div>
</template>
<script setup>
import logger from "debug";
import { onMounted } from "vue";
const debug = logger("travvir:blogs:DemoViewer.vue");
//variables
const emit = defineEmits(["marzipanoReady"]);
const viewers = reactive({});
let marzipanoViewer = ref(null);
const marzipanoReady = ref(false);
const config = useRuntimeConfig();
const { $isAppleChrome, $isAppleWebview, $isIPhone, $isMacSafari, $marzipano } =
  useNuxtApp();

const props = defineProps({
  panoId: {
    type: String,
    default: "explorePano1",
  },
  imageSrc: {
    type: String,
    required: true,
  },
  drag: {
    type: Boolean,
    default: false,
  },
  scrollZoom: {
    type: Boolean,
    default: true,
  },
  onlyPreview: {
    type: Boolean,
    default: false,
  },
  customFov: {
    type: Boolean,
    default: false,
  },
});

const panoramas = reactive({
  makemytrip: {
    id: "61fcc50f0428aba3fd5dce60/4b2dd0db-30ab-486b-bf75-9ab8f6b40c36",
    name: "Hotel Room(TEk0ZaPBLv)",
  },
  swiggy: {
    id: "682c31c4ece8ca2fc66670e5/9576cf27-d2ec-46ca-bf2d-9efe9ebb9682",
    name: "Restaurant",
  },
  gym: {
    id: "61fcc50f0428aba3fd5dce60/ac5b4275-a1d2-426a-88a0-201e65405ac0",
    name: "Gym(SoAD0NVqMO)",
  },
  wework: {
    id: "67b83c0fb5b6da2b18cb7e95/defd4948-ce49-465b-8d96-4171f02f73b3",
    name: "Sitting Area",
  },
  villa: {
    id: "651f85af619e7c4b109ff851/534bba1f-d7c3-48bc-b4f4-9c067aa57301",
    name: "Shopping Mart",
  },
  warehousing: {
    id: "62418a08c6067232263b61aa/cb05a43a-9adf-4ba0-af01-97f284f065c1",
    name: "Indoors 1",
  },
  Beach: {
    id: "666191b7d45d6954a987415a/67aa97e1-d7a8-4fe3-964c-02ab5e76558f",
    name: "Bar Sitting Area",
  },
});

watch(
  () => props.imageSrc,
  async (newSrc, oldSrc) => {
    debug("Image source changed:", newSrc);
    if (newSrc !== oldSrc) {
      await initPano();
    }
  },
);

//methods
const loadImage = (url) => {
  return new Promise((resolve, reject) => {
    let img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      debug("loaded image", url);
      resolve();
      emit("marzipanoReady", true);
    };
    img.onerror = (error) => {
      console.error("Failed to load image:", url, error);
      reject(new Error(`Failed to load image: ${url}`));
      emit("marzipanoReady", false);
    };
    img.src = url;
  });
};

const loadPanorama = async (viewer, imageSource, panoElement) => {
  try {
    const levels = [
      {
        tileSize: 256,
        size: 256,
        fallbackOnly: true,
      },
      {
        tileSize: 512,
        size: 512,
      },
      {
        tileSize: 512,
        size: 1024,
      },
      {
        tileSize: 512,
        size: 2048,
      },
    ];
    let sequence = "bdflru";
    if ($isAppleChrome || $isAppleWebview || $isMacSafari || $isIPhone) {
      sequence = "urlfdb";
    }

    const geometry = new $marzipano.CubeGeometry(levels);
    const source = $marzipano.ImageUrlSource.fromString(
      `${imageSource}/{z}/{f}/{y}/{x}.jpg`,
      {
        cubeMapPreviewUrl: `${imageSource}/preview.jpg`,
        cubeMapPreviewFaceOrder: sequence,
      },
    );
    console.log("Created geometry and source for panorama", imageSource);
    const initialView = {
      fov: props.customFov ? (59 * Math.PI) / 180 : (90 * Math.PI) / 180,
    };
    var limiter = $marzipano.RectilinearView.limit.traditional(
      1024,
      (120 * Math.PI) / 180,
    );
    const view = new $marzipano.RectilinearView(initialView, limiter);

    const scene = viewer.createScene({
      source: source,
      geometry: geometry,
      view: view,
      pinFirstLevel: true,
    });

    scene.switchTo({ transitionDuration: 1000 });
    // viewer.controls().disable();
    if (props.drag) {
      viewer.controls().enable();
    }
    console.log("Loading panorama image...");
    await loadImage(`${imageSource}/preview.jpg`);

    const resizeObserver = new ResizeObserver(() => {
      viewer.updateSize();
    });
    resizeObserver.observe(panoElement);
    // var autorotate = $marzipano.autorotate({
    //   yawSpeed: 0.15,
    //   targetPitch: 0,
    // });
    // viewer.startMovement(autorotate);
  } catch (error) {
    console.error("Error in loadPanorama:", error);
    throw error;
  }
};

const initPano = async () => {
  marzipanoReady.value = false;
  const panoElement = document.getElementById(props.panoId);

  if (!panoElement) {
    console.error("No Canvas Container element found");
    return;
  }

  try {
    const imageSource = props.imageSrc;
    const viewerOpts = {
      controls: { mouseViewMode: "drag", scrollZoom: false },
    };
    const viewer = new $marzipano.Viewer(panoElement, viewerOpts);
    await loadPanorama(viewer, imageSource, panoElement);
    console.log(`Successfully loaded panorama: ${viewer}`);
  } catch (error) {
    console.error(`Failed to load panorama:`, error);
  }
  marzipanoReady.value = true;
  debug(marzipanoReady.value, "ready", viewers);
};

onMounted(async () => {
  await initPano();
});
</script>
<style scoped>
.pano {
  width: 100%;
  height: 100%;
  overflow: hidden;
}
</style>
